import React, { useEffect, useState, useCallback } from "react";
import api from "../../utils/axiosInstance";
import {
  FaCloud, FaCheckCircle, FaTimesCircle, FaSpinner, FaFolder,
  FaTrash, FaChevronLeft, FaImage, FaFileVideo, FaFilePdf, FaFile,
  FaSearch, FaClock, FaFolderOpen, FaFileAudio, FaExternalLinkAlt,
  FaCog, FaDatabase, FaChevronRight,
} from "react-icons/fa";
import { Card, CardBody, Spinner } from "../../components/ui";
import { useModal } from "../../context/ModalContext";

/* ── Settings Tab ── */
function StorageSettings() {
  const [form, setForm] = useState({
    provider: "r2", accountId: "", accessKeyId: "", secretAccessKey: "",
    bucketName: "", region: "auto", publicUrl: "", enabled: true,
  });
  const [hasExisting, setHasExisting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    api.get("/api/admin/storage")
      .then((res) => { if (res.data.data) { setForm((prev) => ({ ...prev, ...res.data.data })); setHasExisting(true); } })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/api/admin/storage", form);
      setHasExisting(true);
      setTestResult({ success: true, message: "Settings saved successfully." });
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.error || "Failed to save" });
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await api.post("/api/admin/storage/test?global=true");
      setTestResult({ success: true, message: res.data.message });
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.error || "Connection test failed" });
    } finally { setTesting(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-ink-muted p-8"><FaSpinner className="animate-spin" /> Loading...</div>;

  return (
    <div className="max-w-2xl">
      <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800 mb-6">
        This is the <strong>global default</strong> storage. All WhatsApp media will be stored here unless a user configures their own bucket.
      </div>
      <form onSubmit={handleSave} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="input-group">
            <label className="input-label">Provider</label>
            <select className="input" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}>
              <option value="r2">Cloudflare R2</option>
              <option value="s3">Amazon S3</option>
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Region</label>
            <input className="input" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="auto" />
          </div>
        </div>
        {form.provider === "r2" && (
          <div className="input-group">
            <label className="input-label">Cloudflare Account ID</label>
            <input className="input" value={form.accountId} onChange={(e) => setForm({ ...form, accountId: e.target.value })} placeholder="Found in your Cloudflare dashboard URL" />
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="input-group">
            <label className="input-label">Access Key ID</label>
            <input className="input" value={form.accessKeyId} onChange={(e) => setForm({ ...form, accessKeyId: e.target.value })} required />
          </div>
          <div className="input-group">
            <label className="input-label">Secret Access Key</label>
            <input className="input" type="password" value={form.secretAccessKey} onChange={(e) => setForm({ ...form, secretAccessKey: e.target.value })} placeholder={hasExisting ? "Leave blank to keep current" : ""} required={!hasExisting} />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="input-group">
            <label className="input-label">Bucket Name</label>
            <input className="input" value={form.bucketName} onChange={(e) => setForm({ ...form, bucketName: e.target.value })} placeholder="walytic-media" required />
          </div>
          <div className="input-group">
            <label className="input-label">Public URL (Custom Domain)</label>
            <input className="input" value={form.publicUrl} onChange={(e) => setForm({ ...form, publicUrl: e.target.value })} placeholder="https://media.yourdomain.com" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-ink-primary cursor-pointer select-none">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="rounded" /> Enabled
        </label>
        <div className="flex items-center gap-3 pt-2">
          <button className="btn-primary" type="submit" disabled={saving}>{saving ? "Saving..." : "Save Settings"}</button>
          <button type="button" className="btn-secondary" onClick={handleTest} disabled={testing || !hasExisting}>{testing ? "Testing..." : "Test Connection"}</button>
        </div>
        {testResult && (
          <div className={`flex items-center gap-2 text-sm mt-2 p-3 rounded-lg border ${testResult.success ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}>
            {testResult.success ? <FaCheckCircle /> : <FaTimesCircle />} {testResult.message}
          </div>
        )}
      </form>
    </div>
  );
}

/* ── Helpers ── */
function getFileIcon(key, size) {
  const ext = key.split(".").pop().toLowerCase();
  const iconSize = size || 16;
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return <FaImage size={iconSize} className="text-blue-500" />;
  if (["mp4", "3gp", "webm", "mov"].includes(ext)) return <FaFileVideo size={iconSize} className="text-purple-500" />;
  if (["ogg", "mp3", "m4a", "wav"].includes(ext)) return <FaFileAudio size={iconSize} className="text-green-500" />;
  if (ext === "pdf") return <FaFilePdf size={iconSize} className="text-red-500" />;
  return <FaFile size={iconSize} className="text-ink-faint" />;
}

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
  return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

function timeAgo(date) {
  const d = new Date(date);
  const now = new Date();
  const diff = now - d;
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: days > 365 ? "numeric" : undefined });
}

const BULK_OPTIONS = [
  { label: "1 month", days: 30, color: "text-amber-600 border-amber-200 hover:bg-amber-50" },
  { label: "3 months", days: 90, color: "text-orange-600 border-orange-200 hover:bg-orange-50" },
  { label: "6 months", days: 180, color: "text-red-500 border-red-200 hover:bg-red-50" },
  { label: "1 year", days: 365, color: "text-red-600 border-red-200 hover:bg-red-50" },
];

/* ── Browse Tab ── */
function StorageBrowser() {
  const modal = useModal();
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [prefix, setPrefix] = useState("media/");
  const [searchPrefix, setSearchPrefix] = useState("media/");
  const [nextToken, setNextToken] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(null);
  const [totalSize, setTotalSize] = useState(0);

  const browse = useCallback(async (pfx, token = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ prefix: pfx, maxKeys: 100 });
      if (token) params.set("token", token);
      const res = await api.get(`/api/admin/storage/browse?${params}`);
      const data = res.data;
      if (token) {
        setObjects((prev) => [...prev, ...data.objects]);
      } else {
        setObjects(data.objects);
      }
      setTotalSize((prev) => token
        ? prev + data.objects.reduce((s, o) => s + (o.size || 0), 0)
        : data.objects.reduce((s, o) => s + (o.size || 0), 0)
      );
      setNextToken(data.nextToken);
    } catch (err) {
      console.error("Browse error:", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { browse(prefix); }, [prefix, browse]);

  const handleSearch = (e) => { e.preventDefault(); setPrefix(searchPrefix); setSelected(new Set()); };

  const toggleSelect = (key) => {
    setSelected((prev) => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });
  };

  const selectAll = () => {
    setSelected(selected.size === objects.length ? new Set() : new Set(objects.map((o) => o.key)));
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!(await modal.confirm(`Delete ${selected.size} file(s)? This cannot be undone.`, { danger: true, confirmText: "Delete" }))) return;
    setDeleting(true);
    try {
      const res = await api.delete("/api/admin/storage/files", { data: { keys: [...selected] } });
      if (res.data.success) { setObjects((prev) => prev.filter((o) => !selected.has(o.key))); setSelected(new Set()); }
    } catch (err) { console.error("Delete error:", err); }
    finally { setDeleting(false); }
  };

  const bulkDelete = async (days) => {
    const label = BULK_OPTIONS.find((o) => o.days === days)?.label || `${days} days`;
    if (!(await modal.confirm(`Delete all files older than ${label}? This is irreversible and may take a while.`, { danger: true, confirmText: "Delete All" }))) return;
    setBulkDeleting(days);
    try {
      const res = await api.delete("/api/admin/storage/older-than", { data: { days, prefix: "media/" } });
      if (res.data.success) browse(prefix);
    } catch (err) { console.error("Bulk delete error:", err); }
    finally { setBulkDeleting(null); }
  };

  // Extract "folders" from keys
  const folders = new Set();
  const files = [];
  const prefixDepth = prefix.split("/").filter(Boolean).length;
  objects.forEach((obj) => {
    const parts = obj.key.split("/").filter(Boolean);
    if (parts.length > prefixDepth + 1) {
      folders.add(parts.slice(0, prefixDepth + 1).join("/") + "/");
    } else {
      files.push(obj);
    }
  });

  const navigateTo = (folder) => { setPrefix(folder); setSearchPrefix(folder); setSelected(new Set()); };

  const goUp = () => {
    const parts = prefix.split("/").filter(Boolean);
    if (parts.length > 0) { parts.pop(); navigateTo(parts.length > 0 ? parts.join("/") + "/" : ""); }
  };

  const breadcrumbs = prefix.split("/").filter(Boolean);

  return (
    <div className="space-y-3">
      {/* Navigation bar — breadcrumbs + search + stats */}
      <Card>
        <CardBody className="!py-2.5 !px-4 space-y-2">
          <div className="flex items-center justify-between gap-3">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-1 text-sm min-w-0 overflow-hidden">
              {prefix && (
                <button onClick={goUp} className="flex-shrink-0 p-1 rounded hover:bg-surface-hover text-ink-muted hover:text-ink-primary mr-0.5" title="Go up">
                  <FaChevronLeft size={11} />
                </button>
              )}
              <button onClick={() => navigateTo("")} className="flex-shrink-0 text-ink-muted hover:text-brand">
                <FaCloud size={13} />
              </button>
              {breadcrumbs.map((part, i) => (
                <span key={i} className="flex items-center gap-1 min-w-0">
                  <FaChevronRight size={8} className="text-ink-faint flex-shrink-0" />
                  <button
                    onClick={() => navigateTo(breadcrumbs.slice(0, i + 1).join("/") + "/")}
                    className={`truncate hover:text-brand ${i === breadcrumbs.length - 1 ? "text-ink-primary font-medium" : "text-ink-muted"}`}
                  >
                    {part}
                  </button>
                </span>
              ))}
            </div>

            {/* Stats + actions */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-2xs text-ink-faint hidden sm:flex items-center gap-1">
                <FaDatabase size={9} /> {formatBytes(totalSize)} / {objects.length} objects
              </span>
              {objects.length > 0 && (
                <button onClick={selectAll} className="text-xs text-ink-muted hover:text-ink-primary">
                  {selected.size === objects.length ? "Deselect" : "Select all"}
                </button>
              )}
              {selected.size > 0 && (
                <button
                  onClick={deleteSelected}
                  disabled={deleting}
                  className="text-xs flex items-center gap-1.5 text-red-600 hover:text-red-700 font-medium"
                >
                  <FaTrash size={10} /> {deleting ? "Deleting..." : `Delete (${selected.size})`}
                </button>
              )}
            </div>
          </div>

          {/* Inline search */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" size={10} />
              <input
                className="w-full text-xs py-1.5 pl-7 pr-3 rounded-md border border-surface-border bg-surface-bg text-ink-primary placeholder:text-ink-faint focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20"
                value={searchPrefix}
                onChange={(e) => setSearchPrefix(e.target.value)}
                placeholder="Go to path, e.g. media/916295455902/"
              />
            </div>
            <button type="submit" className="text-xs px-3 py-1.5 rounded-md bg-brand text-white hover:bg-brand/90">Go</button>
          </form>
        </CardBody>
      </Card>

      {/* Cleanup card */}
      <Card>
        <CardBody className="!py-3 !px-4">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-xs font-medium text-ink-secondary flex items-center gap-1.5">
              <FaClock size={11} className="text-ink-faint" /> Remove files older than:
            </span>
            <div className="flex gap-2">
              {BULK_OPTIONS.map((opt) => (
                <button
                  key={opt.days}
                  onClick={() => bulkDelete(opt.days)}
                  disabled={bulkDeleting !== null}
                  className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors disabled:opacity-50 ${opt.color}`}
                >
                  {bulkDeleting === opt.days ? (
                    <span className="flex items-center gap-1"><FaSpinner className="animate-spin" size={10} /> Cleaning...</span>
                  ) : opt.label}
                </button>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Loading state */}
      {loading && objects.length === 0 && (
        <div className="flex items-center justify-center py-16"><Spinner size="lg" /></div>
      )}

      {/* Empty state */}
      {!loading && objects.length === 0 && (
        <div className="text-center py-16">
          <FaFolderOpen className="mx-auto text-ink-faint mb-3" size={32} />
          <p className="text-sm text-ink-muted">No files found at this path</p>
          <p className="text-xs text-ink-faint mt-1">Try browsing a different prefix or check your storage settings</p>
        </div>
      )}

      {/* File browser table */}
      {(folders.size > 0 || files.length > 0) && (
        <Card>
          <div className="divide-y divide-surface-border">
            {/* Column header */}
            <div className="grid grid-cols-[auto_1fr_80px_90px_40px] gap-3 items-center px-4 py-2 text-2xs font-semibold text-ink-faint uppercase tracking-wider bg-surface-bg">
              <div className="w-5 flex items-center justify-center">
                {files.length > 0 && (
                  <input
                    type="checkbox"
                    checked={files.length > 0 && files.every((f) => selected.has(f.key))}
                    onChange={selectAll}
                    className="accent-[var(--color-brand)] rounded cursor-pointer"
                    title={selected.size === files.length ? "Deselect all" : "Select all"}
                  />
                )}
              </div>
              <div>Name</div>
              <div className="text-right">Size</div>
              <div className="text-right">Modified</div>
              <div />
            </div>

            {/* Folders */}
            {[...folders].sort().map((folder) => {
              const folderName = folder.replace(prefix, "").replace(/\/$/, "");
              return (
                <button
                  key={folder}
                  onClick={() => navigateTo(folder)}
                  className="w-full grid grid-cols-[auto_1fr_80px_90px_40px] gap-3 items-center px-4 py-3 hover:bg-surface-hover text-left group transition-colors"
                >
                  <div className="w-5 flex items-center justify-center">
                    <FaFolder className="text-amber-400" size={15} />
                  </div>
                  <div className="truncate text-sm text-ink-primary group-hover:text-brand font-medium">{folderName}</div>
                  <div className="text-xs text-ink-faint text-right">--</div>
                  <div className="text-xs text-ink-faint text-right">--</div>
                  <div className="flex justify-center">
                    <FaChevronRight size={10} className="text-ink-faint group-hover:text-brand" />
                  </div>
                </button>
              );
            })}

            {/* Files */}
            {files.map((obj) => {
              const fileName = obj.key.split("/").pop();
              const isSelected = selected.has(obj.key);
              return (
                <div
                  key={obj.key}
                  onClick={() => toggleSelect(obj.key)}
                  className={`grid grid-cols-[auto_1fr_80px_90px_40px] gap-3 items-center px-4 py-3 cursor-pointer group transition-colors ${
                    isSelected ? "bg-brand/5" : "hover:bg-surface-hover"
                  }`}
                >
                  <div className="w-5 flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(obj.key)}
                      className="accent-[var(--color-brand)] rounded cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="flex items-center gap-2.5 min-w-0">
                    {getFileIcon(obj.key, 14)}
                    <div className="min-w-0">
                      <p className="text-sm text-ink-primary truncate">{fileName}</p>
                    </div>
                  </div>
                  <div className="text-xs text-ink-muted text-right tabular-nums">{formatBytes(obj.size)}</div>
                  <div className="text-xs text-ink-faint text-right">{timeAgo(obj.lastModified)}</div>
                  <div className="flex justify-center">
                    <a
                      href={obj.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 rounded text-ink-faint opacity-0 group-hover:opacity-100 hover:text-brand transition-all"
                      onClick={(e) => e.stopPropagation()}
                      title="Open file"
                    >
                      <FaExternalLinkAlt size={10} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Load more */}
      {nextToken && (
        <div className="text-center pt-2">
          <button onClick={() => browse(prefix, nextToken)} disabled={loading} className="btn-secondary btn-sm px-6">
            {loading ? <span className="flex items-center gap-2"><FaSpinner className="animate-spin" size={10} /> Loading...</span> : "Load More Files"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Main Page ── */
export default function AdminStorage() {
  const [tab, setTab] = useState("browse");

  const tabs = [
    { key: "browse", label: "Browse Files", icon: FaFolderOpen },
    { key: "settings", label: "Settings", icon: FaCog },
  ];

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center">
          <FaCloud className="text-brand" size={18} />
        </div>
        <div>
          <h1 className="text-lg font-bold text-ink-primary">Cloud Storage</h1>
          <p className="text-xs text-ink-muted">Browse, manage, and clean up your R2 media files</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-surface-border mb-5">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? "border-brand text-brand"
                : "border-transparent text-ink-muted hover:text-ink-primary"
            }`}
          >
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "browse" && <StorageBrowser />}
      {tab === "settings" && <StorageSettings />}
    </div>
  );
}
